import { UPLOAD_MAX_BYTES, type ImportTranscriptInput } from '@sl/shared';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { meetingsApi } from '../lib/api.js';
import { parseTranscriptImport, TranscriptImportError } from '../lib/transcriptImport.js';

export function UploadPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const importFileRef = useRef<HTMLInputElement>(null);
  const [importText, setImportText] = useState('');
  const [importProviderChoice, setImportProviderChoice] = useState<'imported' | 'zoom_notes'>(
    'imported',
  );
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSkipped, setImportSkipped] = useState<number | null>(null);

  const { data } = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => meetingsApi.get(id),
    enabled: Boolean(id),
  });

  const accept = (picked: File | undefined): void => {
    if (!picked) return;
    if (picked.size > UPLOAD_MAX_BYTES) {
      setError(t('upload.tooBig'));
      return;
    }
    setError(null);
    setFile(picked);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    accept(e.dataTransfer.files[0]);
  };

  const submit = async (): Promise<void> => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await meetingsApi.upload(id, file, setProgress);
      setDone(true);
      navigate(`/meetings/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const onImportFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const reader = new FileReader();
    reader.onload = () => setImportText(String(reader.result ?? ''));
    reader.readAsText(picked);
  };

  const submitImport = async (): Promise<void> => {
    setImportBusy(true);
    setImportError(null);
    setImportSkipped(null);
    try {
      const parsed = parseTranscriptImport(importText);
      if (parsed.segments.length === 0) {
        setImportError(t('upload.importEmpty'));
        return;
      }
      const body: ImportTranscriptInput = {
        provider: parsed.provider ?? importProviderChoice,
        ...(parsed.model ? { model: parsed.model } : {}),
        ...(parsed.language ? { language: parsed.language } : {}),
        ...(parsed.speakers ? { speakers: parsed.speakers } : {}),
        segments: parsed.segments,
      };
      await meetingsApi.importTranscript(id, body);
      setImportSkipped(parsed.skipped);
      navigate(`/meetings/${id}`);
    } catch (err) {
      setImportError(
        err instanceof TranscriptImportError || err instanceof Error
          ? err.message
          : t('common.error'),
      );
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <>
      <h1>{data?.meeting.title ?? t('upload.title')}</h1>
      {error && <div className="banner error">{error}</div>}
      {done && <div className="banner">{t('upload.done')}</div>}

      <div
        className="card"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.click();
        }}
        style={{ textAlign: 'center', cursor: 'pointer', padding: 32 }}
      >
        <p style={{ margin: 0 }}>{file ? file.name : t('upload.drop')}</p>
        {file && (
          <p className="muted" style={{ marginBottom: 0 }}>
            {(file.size / (1024 * 1024)).toFixed(1)} MB
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,video/*"
          hidden
          onChange={(e) => accept(e.target.files?.[0])}
        />
      </div>

      {busy && (
        <div className="card">
          <p className="muted">
            {t('upload.uploading')} — {progress}%
          </p>
          <div style={{ background: 'var(--ivory-2)', borderRadius: 999, height: 8 }}>
            <div
              style={{
                width: `${progress}%`,
                background: 'var(--gold)',
                height: 8,
                borderRadius: 999,
                transition: 'width 120ms linear',
              }}
            />
          </div>
        </div>
      )}

      <button type="button" className="gold" disabled={!file || busy} onClick={() => void submit()}>
        {t('upload.submit')}
      </button>

      <h2 style={{ marginTop: 28 }}>{t('upload.importTitle')}</h2>
      <p className="muted">{t('upload.importIntro')}</p>
      {importError && <div className="banner error">{importError}</div>}
      {importSkipped !== null && importSkipped > 0 && (
        <div className="banner">{t('upload.importSkipped', { count: importSkipped })}</div>
      )}

      <div className="card">
        <div className="field">
          <label htmlFor="importText">{t('upload.importPasteLabel')}</label>
          <textarea
            id="importText"
            dir="auto"
            rows={8}
            value={importText}
            placeholder={t('upload.importPastePlaceholder') as string}
            onChange={(e) => setImportText(e.target.value)}
          />
        </div>

        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="field">
            <label htmlFor="importFile">{t('upload.importFileLabel')}</label>
            <input
              ref={importFileRef}
              id="importFile"
              type="file"
              accept=".vtt,.txt,.json,text/vtt,text/plain,application/json"
              onChange={onImportFile}
            />
          </div>
          <div className="field">
            <label htmlFor="importProvider">{t('upload.importProviderLabel')}</label>
            <select
              id="importProvider"
              value={importProviderChoice}
              onChange={(e) => setImportProviderChoice(e.target.value as typeof importProviderChoice)}
            >
              <option value="imported">{t('upload.importProviderOther')}</option>
              <option value="zoom_notes">{t('upload.importProviderZoom')}</option>
            </select>
          </div>
        </div>

        <button
          type="button"
          className="secondary"
          disabled={importBusy || !importText.trim()}
          onClick={() => void submitImport()}
        >
          {t('upload.importSubmit')}
        </button>
      </div>
    </>
  );
}
